/**
 * Analytics types for MusicMagic.io
 * PostHog event tracking and user journey analytics
 */

import { PlaylistFilters } from './common';

// Base analytics event interface
export interface BaseAnalyticsEvent {
  user_id?: string;           // PostHog distinct_id
  session_id: string;         // Session identifier
  page_url: string;           // Current page URL
  referrer?: string;          // Referrer URL
  locale: string;             // Current locale (en, es, de, etc.)
  timestamp: number;          // Event timestamp
  user_agent: string;         // Browser/device info
}

// Playlist Generator Analytics Events
export interface PlaylistGeneratorAnalyticsEvent extends BaseAnalyticsEvent {
  // Configuration state (referencing existing GeneratorAccountOption/GenreOption types)
  selected_accounts?: string[];        // Account slugs from GeneratorAccountOption
  selected_genres?: string[];          // Genre slugs from GeneratorGenreOption
  recently_discovered?: boolean;       // Toggle state
  most_popular?: boolean;              // Toggle state
  valence?: number;                    // 0-1 value
  
  // Context
  step: 'entry' | 'config' | 'preview' | 'integration';
  total_tracks?: number;               // Available tracks count
  config_completeness?: number;        // 0-1 completion score
}

// Filter/Search Analytics Events
export interface FilterAnalyticsEvent extends BaseAnalyticsEvent {
  filter_type: 'category' | 'tag' | 'genre' | 'account' | 'artist' | 'search';
  filter_value: string;                // Selected filter value
  filter_slug: string;                 // URL-safe slug
  active_filters: PlaylistFilters;     // Reuse existing PlaylistFilters interface
  results_count: number;               // Results after filter
  page_number: number;                 // Current page
}

// Spotify Integration Analytics Events
export interface SpotifyAnalyticsEvent extends BaseAnalyticsEvent {
  auth_source: 'header' | 'generator' | 'profile' | 'playlist_detail';
  playlist_uuid?: string;              // Generated playlist ID
  spotify_playlist_id?: string;        // Spotify playlist ID
  error_code?: string;                 // Error identifier
  auth_duration?: number;              // Time to complete auth
}

// Content Engagement Analytics Events
export interface ContentAnalyticsEvent extends BaseAnalyticsEvent {
  content_type: 'playlist' | 'blog_post' | 'tag_page' | 'category_page';
  content_slug: string;                // Content identifier
  content_title?: string;              // Content title
  engagement_type: 'view' | 'click' | 'share' | 'copy_link' | 'play_track';
  position?: number;                   // Position in list/grid
  section?: string;                    // Page section identifier
}

// Navigation Analytics Events
export interface NavigationAnalyticsEvent extends BaseAnalyticsEvent {
  navigation_type: 'header' | 'footer' | 'breadcrumb' | 'pagination' | 'internal_link';
  destination_url: string;             // Where user navigated to
  source_section?: string;             // Where navigation originated
  link_text?: string;                  // Link text or button label
}

// PostHog Client Configuration
export interface PostHogClientConfig {
  apiKey: string;
  apiHost: string;
  autocapture: boolean;
  capture_pageview: boolean;
  session_recording: boolean;
  disable_session_recording?: boolean;
  debug?: boolean;
  loaded?: (posthog: any) => void;
}

// Analytics Event Data Structure
export interface AnalyticsEventData {
  event: string;
  properties: Record<string, any>;
  timestamp?: number;
}

// Analytics Context Interface
export interface AnalyticsContext {
  isEnabled: boolean;
  userId?: string;
  sessionId: string;
  locale: string;
  pageUrl: string;
  referrer?: string;
}

// Event Names Enum for consistency
export enum AnalyticsEventNames {
  // Page Views & Navigation
  PAGE_VIEWED = 'page_viewed',
  NAVIGATION_CLICKED = 'navigation_clicked',
  
  // Playlist Generator
  PLAYLIST_GENERATOR_VIEWED = 'playlist_generator_viewed',
  ACCOUNT_SELECTED = 'account_selected',
  ACCOUNT_DESELECTED = 'account_deselected',
  GENRE_SELECTED = 'genre_selected',
  GENRE_DESELECTED = 'genre_deselected',
  TOGGLE_CHANGED = 'toggle_changed',
  VALENCE_ADJUSTED = 'valence_adjusted',
  TRACK_COUNT_VIEWED = 'track_count_viewed',
  BUILD_PLAYLIST_CLICKED = 'build_playlist_clicked',
  PLAYLIST_PREVIEW_VIEWED = 'playlist_preview_viewed',
  SPOTIFY_ADD_ATTEMPTED = 'spotify_add_attempted',
  SPOTIFY_ADD_COMPLETED = 'spotify_add_completed',
  PLAYLIST_GENERATION_FAILED = 'playlist_generation_failed',
  
  // Search & Filtering
  SEARCH_PERFORMED = 'search_performed',
  FILTER_APPLIED = 'filter_applied',
  FILTER_REMOVED = 'filter_removed',
  PAGINATION_CLICKED = 'pagination_clicked',
  SORT_CHANGED = 'sort_changed',
  
  // Spotify Integration
  SPOTIFY_AUTH_STARTED = 'spotify_auth_started',
  SPOTIFY_AUTH_COMPLETED = 'spotify_auth_completed',
  SPOTIFY_AUTH_FAILED = 'spotify_auth_failed',
  SPOTIFY_SIGNOUT = 'spotify_signout',
  
  // Content Engagement
  PLAYLIST_VIEWED = 'playlist_viewed',
  TRACK_PLAYED = 'track_played',
  SHARE_CLICKED = 'share_clicked',
  COPY_LINK_CLICKED = 'copy_link_clicked',
}

// Union type for all analytics events
export type AnalyticsEvent = 
  | PlaylistGeneratorAnalyticsEvent
  | FilterAnalyticsEvent
  | SpotifyAnalyticsEvent
  | ContentAnalyticsEvent
  | NavigationAnalyticsEvent; 